<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <div class="instances-system-view-switcher" role="group" aria-label="Tipo de vista">
                <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': systemViewMode == 'cards' }" v-b-tooltip.hover.bottom title="Vista de tarjetas" @click="systemViewMode = 'cards'"><AppIcon name="view_module" :fill="systemViewMode == 'cards'" /></b-button>
                <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': systemViewMode == 'tree' }" v-b-tooltip.hover.bottom title="Vista en árbol" @click="systemViewMode = 'tree'"><AppIcon name="account_tree" :fill="systemViewMode == 'tree'" /></b-button>
                <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': systemViewMode == 'full' }" v-b-tooltip.hover.bottom title="Vista completa (todo desplegado)" @click="systemViewMode = 'full'"><AppIcon name="view_list" :fill="systemViewMode == 'full'" /></b-button>
              </div>
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom
                        title="Agregar sistema" aria-label="Agregar sistema" @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 240px; max-width: 340px;">
              <label class="filter-label">Buscar</label>
              <b-form-input type="text" size="sm" placeholder="Nombre o dominio" v-model="filters.name" v-on:keyup.enter.native="filter" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 310px;">
              <label class="filter-label">Cliente</label>
              <v-select :options="arr.select.customers" v-model="filters.customer" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 300px;">
              <label class="filter-label">Agrupación</label>
              <v-select :options="arr.select.categories" v-model="filters.categories" placeholder="Todas" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 210px; max-width: 290px;">
              <label class="filter-label">Servidor</label>
              <v-select :options="arr.select.instances_servers" v-model="filters.instances_server" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 320px;">
              <label class="filter-label">Estado operativo</label>
              <v-select :options="arr.filter.health" v-model="filters.health" :clearable="false" :multiple="false" :select-on-tab="true" />
            </div>
            <b-button variant="outline-dark" @click="filter()" size="sm" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">  
        <b-row>
          <b-col>
            <div v-if="table.isBusy" class="instances-system-loader" role="status" aria-live="polite">
              <b-spinner variant="primary" label="Cargando sistemas"></b-spinner>
              <span>Cargando sistemas...</span>
            </div>
            <div v-else-if="table.items.length" class="instances-system-groups">
              <b-card v-for="group in groupedSystems" :key="group.id" no-body class="instances-system-group-card">
                <button type="button" class="instances-system-group-card__header" :class="{ 'instances-system-group-card__header--static': systemViewMode == 'full' }" @click="toggleSystemGroup(group.id)">
                  <span class="instances-system-group-card__icon"><AppIcon name="folder" font-scale="1.2" /></span>
                  <div class="instances-system-group-card__summary">
                    <h3>{{group.name}}</h3>
                    <span>{{group.items.length}} {{group.items.length == 1 ? 'sistema' : 'sistemas'}}</span>
                  </div>
                  <div class="instances-system-group-card__health" :aria-label="getSystemGroupHealthLabel(group.health)">
                    <span v-if="group.health.withoutBackups" class="instances-system-group-card__health-item instances-system-group-card__health-item--warning" v-b-tooltip.hover :title="group.health.withoutBackups + ' sistema(s) sin backups activos'">
                      <AppIcon name="backup" /> {{group.health.withoutBackups}} sin backups
                    </span>
                    <span v-if="group.health.accessProblems" class="instances-system-group-card__health-item instances-system-group-card__health-item--danger" v-b-tooltip.hover :title="group.health.accessProblems + ' sistema(s) con problemas de acceso'">
                      <AppIcon name="link_off" /> {{group.health.accessProblems}} acceso
                    </span>
                    <span v-if="group.health.dnsProblems" class="instances-system-group-card__health-item instances-system-group-card__health-item--danger" v-b-tooltip.hover :title="group.health.dnsProblems + ' sistema(s) con alertas DNS'">
                      <AppIcon name="language" /> {{group.health.dnsProblems}} DNS
                    </span>
                    <span v-if="group.health.unblocked" class="instances-system-group-card__health-item instances-system-group-card__health-item--danger" v-b-tooltip.hover :title="group.health.unblocked + ' sistema(s) desbloqueado(s)'">
                      <AppIcon name="lock_open" /> {{group.health.unblocked}} desbloqueados
                    </span>
                    <span v-if="group.health.processing" class="instances-system-group-card__health-item instances-system-group-card__health-item--info" v-b-tooltip.hover :title="group.health.processing + ' sistema(s) en instalación o actualización'">
                      <AppIcon name="progress_activity" class="instances-process-indicator__icon" /> {{group.health.processing}} procesando
                    </span>
                    <span v-if="!getSystemGroupPriority(group.health)" class="instances-system-group-card__health-item instances-system-group-card__health-item--success">
                      <AppIcon name="check_circle" /> Saludable
                    </span>
                  </div>
                  <AppIcon v-if="systemViewMode != 'full'" class="instances-system-group-card__toggle" :name="isSystemGroupOpen(group.id) ? 'expand_less' : 'expand_more'" />
                </button>
                <b-collapse :visible="isSystemGroupOpen(group.id)" class="instances-system-group-card__body">
                  <div v-if="systemViewMode == 'cards'" class="instances-system-group-card__cards">
                  <article v-for="system in group.items" :key="system.id" class="instances-system-card"
                           :class="system.blocked ? 'instances-system-card--blocked' : 'instances-system-card--unblocked'">
                    <div class="instances-system-card__heading">
                      <div>
                        <div class="instances-system-card__title">
                          <span class="instances-system-card__id">#{{system.id}}</span>
                          <AppIcon v-if="system.blocked" name="lock" class="text-secondary" label="Bloqueado" font-scale="1.35" />
                          <AppIcon v-else name="lock_open" class="text-danger" label="Desbloqueado" font-scale="1.35" />
                          <h4>{{system.name}}</h4>
                        </div>
                        <div class="instances-system-card__meta">
                          <span v-if="system.customer" class="instances-system-card__customer"><AppIcon name="business" /> {{system.customer.name}}</span>
                          <b-badge v-if="system.customer && !system.customer.active" variant="danger">Cliente inactivo</b-badge>
                          <b-badge v-if="system.service_customer" variant="info" class="instances-system-card__service instances-system-card__tooltip-icon"
                                   v-b-tooltip.hover :title="system.service_customer.name"><AppIcon name="rocket_launch" /> Servicio</b-badge>
                        </div>
                        <div v-if="system.server || system.server_backups || system.status_process" class="instances-system-card__infrastructure">
                          <span v-if="system.server" class="instances-system-card__server"><AppIcon name="dns" /> {{system.server.name}}</span>
                          <span v-if="system.server_backups" class="instances-system-card__server"><AppIcon name="backup" /> {{system.server_backups.name}}</span>
                          <span v-if="system.status_process" class="instances-process-indicator"><AppIcon name="progress_activity" class="instances-process-indicator__icon" /> Procesando</span>
                        </div>
                      </div>
                      <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle">
                        <template #button-content><AppIcon name="more_vert" label="Opciones" /></template>
                        <b-dropdown-item @click="blockedSystem(system)" v-if="!system.blocked"><AppIcon name="lock" /> Bloquear</b-dropdown-item>
                        <b-dropdown-item @click="unlockedSystem(system)" v-else><AppIcon name="lock_open" /> Desbloquear</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="actionSystem(system)"><AppIcon name="terminal" /> Comandos</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="edit(system)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                        <b-dropdown-item @click="remove(system)"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                      </b-dropdown>
                    </div>

                    <div class="instances-system-card__links">
                      <b-link v-if="getSystemUrl(system)" target="_blank" :href="getSystemUrl(system)"><AppIcon name="language" /> Sistema</b-link>
                      <span v-else-if="system.status_install" class="text-danger"><AppIcon name="warning" /> Sin dominio del sistema</span>
                      <b-link v-if="getSiteUrl(system)" target="_blank" :href="getSiteUrl(system)"><AppIcon name="language" /> Sitio web</b-link>
                      <span v-else-if="system.status_install_site" class="text-danger"><AppIcon name="warning" /> Sin dominio del sitio</span>
                    </div>

                    <div class="instances-system-card__metrics">
                      <section class="instances-system-card__section">
                        <span class="instances-system-card__section-label">Sistema</span>
                        <b-badge :id="'system-status-card-' + system.id" :variant="getSystemInstallVariant(system)" class="instances-system-status"><AppIcon :name="getSystemInstallIcon(system)" font-scale="1.35" /> {{getSystemInstallLabel(system)}}</b-badge>
                        <small v-if="system.pwa_status" class="text-success"><AppIcon name="install_mobile" /> PWA instalada</small>
                        <b-popover :target="'system-status-card-' + system.id" triggers="hover" placement="top" v-if="system.system">
                          <template #title><AppIcon name="deployed_code" class="mr-1" /> Sistema</template>
                          <b>Estado:</b> {{getSystemInstallLabel(system)}}<br>
                          <span v-if="getSystemDetails(system, 'system').version"><b>Versión:</b> {{getSystemDetails(system, 'system').version}}<br></span>
                          <span v-if="getSystemDetails(system, 'system').datetime"><b>Última sincronización:</b> {{moment(getSystemDetails(system, 'system').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                        </b-popover>
                      </section>
                      <section class="instances-system-card__section">
                        <span class="instances-system-card__section-label">Sitio web</span>
                        <b-badge :id="'site-status-card-' + system.id" :variant="getSiteInstallVariant(system)" class="instances-system-status"><AppIcon :name="getSiteInstallIcon(system)" font-scale="1.35" /> {{getSiteInstallLabel(system)}}</b-badge>
                        <b-popover :target="'site-status-card-' + system.id" triggers="hover" placement="top" v-if="system.site">
                          <template #title><AppIcon name="language" class="mr-1" /> Sitio web</template>
                          <b>Estado:</b> {{getSiteInstallLabel(system)}}<br>
                          <span v-if="getSystemDetails(system, 'site').version"><b>Versión:</b> {{getSystemDetails(system, 'site').version}}<br></span>
                          <span v-if="getSystemDetails(system, 'site').datetime"><b>Última sincronización:</b> {{moment(getSystemDetails(system, 'site').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                        </b-popover>
                      </section>
                      <section class="instances-system-card__section instances-system-card__section--controls">
                        <span class="instances-system-card__section-label">Controles</span>
                        <div v-if="getSystemChecks(system).length" class="instances-system-checks">
                          <span v-for="check in getSystemChecks(system)" :key="check.key" :id="'system-check-' + check.key + '-' + system.id" class="instances-system-check" :class="getSystemCheckTone(system, check.key)">
                            <AppIcon :name="check.icon" font-scale="1.65" weight="500" /><span>{{check.label}}</span><small>{{getSystemCheckStatus(system, check.key)}}</small>
                            <b-popover :target="'system-check-' + check.key + '-' + system.id" triggers="hover" placement="top">
                              <template #title><AppIcon :name="check.icon" class="mr-1" /> {{check.label}}</template>
                              <b>Estado:</b> {{getSystemCheckStatus(system, check.key)}}<br>
                              <span v-if="check.key == 'backups' && getSystemDetails(system, 'backups').last"><b>Último backup:</b> {{getSystemDetails(system, 'backups').last}}<br></span>
                              <span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain"><b>Sistema:</b> {{getSystemDetails(system, 'access').domain}}<br></span>
                              <span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain_alt"><b>Sistema alternativo:</b> {{getSystemDetails(system, 'access').domain_alt}}<br></span>
                              <span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain_site"><b>Sitio web:</b> {{getSystemDetails(system, 'access').domain_site}}<br></span>
                              <span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain_site_alt"><b>Sitio web alternativo:</b> {{getSystemDetails(system, 'access').domain_site_alt}}<br></span>
                              <span v-if="check.key == 'audit' && getSystemAuditData(system)"><b>Detalle de auditoría:</b><json-tree :data="getSystemAuditData(system)" :level="1" class="intances-audit-pre-json"></json-tree><br></span>
                              <span v-if="check.key == 'dns' && showDnsProxy(system)">
                                <b>Ruta:</b> {{getAccessInfo(system).dns_check_zyncro_path || '/backend/public/storage/check-zyncro'}}<br>
                                <span v-if="getAccessInfo(system).dns_check_zyncro_url"><b>URL:</b> {{getAccessInfo(system).dns_check_zyncro_url}}<br></span>
                                <b>Respuesta esperada:</b> {{getAccessInfo(system).dns_check_zyncro_content || 'true'}}<br>
                              </span>
                              <span v-else-if="check.key == 'dns'"><b>Detalle:</b> {{getDnsStatusDetail(system)}}<br></span>
                              <span v-if="check.key != 'dns' && getSystemDetails(system, check.key).datetime"><b>Última sincronización:</b> {{moment(getSystemDetails(system, check.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span>
                            </b-popover>
                          </span>
                        </div>
                        <span v-else class="text-muted">Sin controles</span>
                      </section>
                    </div>

                    <div v-if="system.observations || system.observations_backups" class="instances-system-card__alerts">
                      <div v-if="system.observations" class="instances-system-card__alert instances-system-card__alert--observation"><AppIcon name="chat" /><div><b>Observaciones</b><span>{{system.observations}}</span></div></div>
                      <div v-if="system.observations_backups" class="instances-system-card__alert instances-system-card__alert--backup"><AppIcon name="shield" /><div><b>Backups</b><span>{{system.observations_backups}}</span></div></div>
                    </div>
                  </article>
                  </div>
                  <div v-else class="instances-system-tree-view">
                    <article v-for="system in group.items" :key="system.id" class="instances-system-tree-view__system">
                      <span class="instances-system-tree-view__branch"><AppIcon name="deployed_code" /></span>
                      <div class="instances-system-tree-view__header"><div class="instances-system-card__title"><span class="instances-system-card__id">#{{system.id}}</span><AppIcon :name="system.blocked ? 'lock' : 'lock_open'" :class="system.blocked ? 'text-secondary' : 'text-danger'" /><h4>{{system.name}}</h4><b-link v-if="getSystemUrl(system)" :href="getSystemUrl(system)" target="_blank" class="instances-system-tree-view__link"><AppIcon name="language" /> Sistema</b-link><b-link v-if="getSiteUrl(system)" :href="getSiteUrl(system)" target="_blank" class="instances-system-tree-view__link"><AppIcon name="public" /> Sitio</b-link><span :id="'system-tree-info-' + system.id" tabindex="0" class="instances-system-tree-view__more"><AppIcon name="info" label="Ver información del sistema" /></span><b-popover :target="'system-tree-info-' + system.id" triggers="hover focus click blur" placement="top" custom-class="instances-tree-info-popover"><template #title><AppIcon name="info" class="mr-1" /> {{system.name}}</template><b v-if="system.customer">Cliente:</b><span v-if="system.customer"> {{system.customer.name}}<br></span><b v-if="system.service_customer">Servicio:</b><span v-if="system.service_customer"> {{system.service_customer.name}}<br></span><b v-if="system.server">Servidor de instalación:</b><span v-if="system.server"> {{system.server.name}}<br></span><b v-if="system.server_backups">Servidor de backups:</b><span v-if="system.server_backups"> {{system.server_backups.name}}<br></span><span v-if="system.status_process"><b>Proceso:</b> En curso<br></span><span v-if="system.observations"><b>Observaciones:</b> {{system.observations}}<br></span><span v-if="system.observations_backups"><b>Observaciones de backups:</b> {{system.observations_backups}}</span></b-popover></div><b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle"><template #button-content><AppIcon name="more_vert" label="Opciones" /></template><b-dropdown-item @click="actionSystem(system)"><AppIcon name="terminal" /> Comandos</b-dropdown-item><b-dropdown-divider /><b-dropdown-item @click="edit(system)"><AppIcon name="edit" /> Editar</b-dropdown-item></b-dropdown></div>
                      <div class="instances-system-tree-view__details">
                        <span v-if="system.status_process" class="instances-process-indicator">
                          <AppIcon name="progress_activity" class="instances-process-indicator__icon" /> Procesando
                        </span>
                        <span :id="'system-tree-system-' + system.id" class="instances-system-tree-view__item" :class="getSystemInstallVariant(system) == 'success' ? 'text-success' : 'text-danger'"><AppIcon name="deployed_code" /><b>Sistema</b><span>{{getSystemInstallLabel(system)}}</span><b-popover :target="'system-tree-system-' + system.id" triggers="hover" placement="top"><template #title>Sistema</template><b>Estado:</b> {{getSystemInstallLabel(system)}}<br><span v-if="getSystemDetails(system, 'system').version"><b>Versión:</b> {{getSystemDetails(system, 'system').version}}<br></span><span v-if="getSystemDetails(system, 'system').datetime"><b>Última sincronización:</b> {{moment(getSystemDetails(system, 'system').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span></b-popover></span>
                        <span :id="'system-tree-site-' + system.id" class="instances-system-tree-view__item" :class="getSiteInstallVariant(system) == 'success' ? 'text-success' : 'text-danger'"><AppIcon name="language" /><b>Sitio</b><span>{{getSiteInstallLabel(system)}}</span><b-popover :target="'system-tree-site-' + system.id" triggers="hover" placement="top"><template #title>Sitio web</template><b>Estado:</b> {{getSiteInstallLabel(system)}}<br><span v-if="getSystemDetails(system, 'site').version"><b>Versión:</b> {{getSystemDetails(system, 'site').version}}<br></span><span v-if="getSystemDetails(system, 'site').datetime"><b>Última sincronización:</b> {{moment(getSystemDetails(system, 'site').datetime).format('DD/MM/YYYY HH:mm:ss')}}</span></b-popover></span>
                        <span v-for="check in getSystemChecks(system)" :key="check.key" :id="'system-tree-' + check.key + '-' + system.id" class="instances-system-tree-view__item" :class="getSystemCheckTone(system, check.key)"><AppIcon :name="check.icon" /><b>{{check.label}}</b><span>{{getSystemCheckStatus(system, check.key)}}</span><b-popover :target="'system-tree-' + check.key + '-' + system.id" triggers="hover" placement="top"><template #title><AppIcon :name="check.icon" class="mr-1" /> {{check.label}}</template><b>Estado:</b> {{getSystemCheckStatus(system, check.key)}}<br><span v-if="check.key == 'backups' && getSystemDetails(system, 'backups').last"><b>Último backup:</b> {{getSystemDetails(system, 'backups').last}}<br></span><span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain"><b>Sistema:</b> {{getSystemDetails(system, 'access').domain}}<br></span><span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain_alt"><b>Sistema alternativo:</b> {{getSystemDetails(system, 'access').domain_alt}}<br></span><span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain_site"><b>Sitio web:</b> {{getSystemDetails(system, 'access').domain_site}}<br></span><span v-if="check.key == 'access' && getSystemDetails(system, 'access').domain_site_alt"><b>Sitio web alternativo:</b> {{getSystemDetails(system, 'access').domain_site_alt}}<br></span><span v-if="check.key == 'audit' && getSystemAuditData(system)"><b>Detalle de auditoría:</b><json-tree :data="getSystemAuditData(system)" :level="1" class="intances-audit-pre-json"></json-tree><br></span><span v-if="check.key == 'dns' && showDnsProxy(system)"><b>Ruta:</b> {{getAccessInfo(system).dns_check_zyncro_path || '/backend/public/storage/check-zyncro'}}<br><span v-if="getAccessInfo(system).dns_check_zyncro_url"><b>URL:</b> {{getAccessInfo(system).dns_check_zyncro_url}}<br></span><b>Respuesta esperada:</b> {{getAccessInfo(system).dns_check_zyncro_content || 'true'}}<br></span><span v-else-if="check.key == 'dns'"><b>Detalle:</b> {{getDnsStatusDetail(system)}}<br></span><span v-if="check.key != 'dns' && getSystemDetails(system, check.key).datetime"><b>Última sincronización:</b> {{moment(getSystemDetails(system, check.key).datetime).format('DD/MM/YYYY HH:mm:ss')}}</span></b-popover></span>
                      </div>
                    </article>
                  </div>
                </b-collapse>
              </b-card>
            </div>
            <b-table v-if="false" id="instances_system_table"
                    class="mb-0"
                    ref="table"
                    responsive="sm"
                    head-variant="dark"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="table.items"
                    :fields="table.fields"                    
                    :current-page="table.currentPage"
                    :per-page="table.perPage"
                    :busy="table.isBusy">
                
                <template v-slot:cell(id)="data">
                  <b>#{{data.item.id}}</b>
                </template>
                
                <template v-slot:cell(name)="data">
                  <b-icon icon="lock" v-if="data.item.blocked" title="Bloqueado"/>
                  <b>{{data.item.name}}</b>
                  <b-badge variant="secondary" class="ml-2" v-if="data.item.categorie">{{data.item.categorie.name}}</b-badge>                  
                                    
                  <span v-if="data.item.status_install">
                    <br>
                    <b-icon v-if="!data.item.status_disable" icon="circle-fill" variant="success" title="Habilitado"></b-icon> 
                    <b-icon v-else icon="circle-fill" variant="danger" title="Deshabilitado"></b-icon> 

                    <span v-if="data.item.domain">                    
                      <b-link target="_blank" :href="'http://'+data.item.domain">
                        https://{{data.item.domain}}
                      </b-link>                    
                    </span>
                    <span v-else>
                      <span v-if="data.item.server.subdomain">
                        <b-link target="_blank" :href="'http://'+data.item.name+'.'+data.item.server.subdomain">
                          https://{{data.item.name}}.{{data.item.server.subdomain}}
                        </b-link>
                      </span>
                      <span v-else class="text-danger">                        
                        [Falta configuración de dominio para el sistema]
                      </span>
                    </span>
                  </span>
                  
                  <span v-if="data.item.status_install_site">
                    <br>
                    <b-icon v-if="!data.item.status_disable_site" icon="circle-fill" variant="success" title="Habilitado"></b-icon> 
                    <b-icon v-else icon="circle-fill" variant="danger" title="Deshabilitado"></b-icon> 

                    <span v-if="data.item.domain_site">
                      <b-link target="_blank" :href="'http://'+data.item.domain_site">
                        https://{{data.item.domain_site}}
                      </b-link>                        
                    </span>
                    <span v-else>
                      <span v-if="data.item.server.subdomain">
                        <b-link target="_blank" :href="'http://site.'+data.item.name+'.'+data.item.server.subdomain">
                          https://{{'site.' + data.item.name}}.{{data.item.server.subdomain}}
                        </b-link>                                                  
                      </span>
                      <span v-else class="text-danger">
                        [Falta configuración de dominio para el sitio]
                      </span>                            
                    </span>          
                  </span>
                </template>

                <template v-slot:cell(customer_id)="data">
                  <div v-if="data.item.customer">            
                    <b-avatar :src="data.item.customer.image"
                              v-if="data.item.customer.image">
                    </b-avatar>
                    <b-avatar v-else 
                              icon="building"                                            
                              variant="dark">
                    </b-avatar>                                                     
                    &nbsp;<b>{{data.item.customer.name}}</b>   
                    
                    <b-icon icon="circle-fill" v-if="!data.item.customer.active" class="ml-2" variant="danger" v-b-tooltip.hover title="Cliente INACTIVO" />                                       
                  </div>
                </template>
                
                <template v-slot:cell(observations)="data">
                  <div v-if="data.item.observations" v-b-tooltip.hover :title="data.item.observations">
                    <b-icon icon="chat-square-text"></b-icon>
                  </div>                           
                </template>

                <template v-slot:cell(services)="data">                  
                  <div v-if="data.item.service_customer" v-b-tooltip.hover :title="data.item.service_customer.name" style="cursor:pointer;">
                    <i class="fa fa-rocket"></i>
                  </div>                           
                </template>                

                <template v-slot:cell(instances_server_id)="data">
                  <span v-if="data.item.server">
                    <b>{{data.item.server.name}}</b><br>
                    <span v-if="data.item.server.subdomain">
                      <b-link target="_blank" :href="'http://'+data.item.server.subdomain">
                        https://{{data.item.server.subdomain}}
                      </b-link>  
                    </span>
                  </span>
                </template>              

                <template v-slot:cell(status_system)="data">                  
                  <div v-if="data.item.status_install" :id="'server-system-' + data.item.id">
                    <div class="text-success font-weight-bold">
                      <b-icon icon="circle-fill" variant="success"></b-icon> Instalado
                    </div>
                  </div>                    
                  <div v-else :id="'server-system-' + data.item.id">
                    <div class="text-danger font-weight-bold">
                      <b-icon icon="circle-fill" variant="danger"></b-icon> No Instalado
                    </div>
                  </div>

                  <div v-if="data.item.pwa_status">
                    <div class="text-success font-weight-bold">
                      <b-icon icon="circle-fill" variant="success"></b-icon> PWA Instalado
                    </div>
                  </div>                    
                  <div v-else>
                    <div class="text-danger font-weight-bold">
                      <b-icon icon="circle-fill" variant="danger"></b-icon> PWA No Instalado
                    </div>
                  </div>

                  <b-popover :target="'server-system-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="data.item.status_install && data.item.system">

                    <template #title>Estado del Sistema</template>                    
                    <b>Versión:</b> {{JSON.parse(data.item.system).version}}<br>
                    <b>Contraseña:</b> {{JSON.parse(data.item.system).pass}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.system).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                         
                </template>                

                <template v-slot:cell(status_site)="data">                  
                  <div v-if="data.item.status_install_site">
                    <div class="text-success font-weight-bold" :id="'server-site-' + data.item.id">
                      <b-icon icon="circle-fill" variant="success"></b-icon> Instalado
                    </div>
                  </div>                    
                  <div v-else :id="'server-site-' + data.item.id">
                    <div class="text-danger font-weight-bold">
                      <b-icon icon="circle-fill" variant="danger"></b-icon> No Instalado
                    </div>
                  </div>

                  <b-popover :target="'server-site-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="data.item.status_install && data.item.status_install_site && data.item.site">

                    <template #title>Estado del Sitio Web</template>                    
                    <b>Versión:</b> {{JSON.parse(data.item.site).version}}<br>                    
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.site).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                    
                </template>                

                <template v-slot:cell(status_process)="data">
                  <div v-if="data.item.status_process">
                    <span class="instances-process-indicator"><AppIcon name="progress_activity" class="instances-process-indicator__icon" /> Procesando</span>
                  </div>
                </template>

                <template v-slot:cell(instances_server_backups_id)="data">
                  <span v-if="data.item.server_backups">
                    <b>{{data.item.server_backups.name}}</b>                    
                  </span>
                  <span v-if="data.item.observations_backups" v-b-tooltip.hover :title="data.item.observations_backups" style="cursor:pointer;">
                    <b-icon variant="danger" icon="shield-fill-exclamation"></b-icon>
                  </span>                    
                </template>

                <template v-slot:cell(backups)="data">
                  <div v-if="data.item.status_install && data.item.backups">
                    <div v-if="showFieldSystem(data.item, 'backups') == 'Activo'" class="text-success font-weight-bold" :id="'server-backups-' + data.item.id">
                      <b-icon icon="circle-fill" variant="success"></b-icon>
                    </div>
                    <div v-if="showFieldSystem(data.item, 'backups') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-backups-' + data.item.id">
                      <b-icon icon="circle-fill" variant="danger"></b-icon>
                    </div>   
                  </div>

                  <b-popover :target="'server-backups-' + data.item.id" 
                              triggers="hover" 
                              placement="top" 
                              v-if="data.item.status_install && data.item.backups">

                    <template #title>Estado del Backups</template>                    
                    <b>Estado:</b> {{JSON.parse(data.item.backups).status}}<br>
                    <b>Últimos Backups:</b> {{JSON.parse(data.item.backups).last}}<br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.backups).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>                   
                </template>

                <template v-slot:cell(audit)="data">
                  <div v-if="data.item.status_install && data.item.audit">
                    <div v-if="showFieldSystem(data.item, 'audit') == 'Activo'" class="text-success font-weight-bold" :id="'server-audit-' + data.item.id">
                      <b-icon icon="circle-fill" variant="success"></b-icon>
                    </div>
                    <div v-if="showFieldSystem(data.item, 'audit') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-audit-' + data.item.id">
                      <b-icon icon="circle-fill" variant="danger"></b-icon>
                    </div>   
                  </div>

                  <b-popover :target="'server-audit-' + data.item.id" 
                              triggers="hover" 
                              placement="top"
                              @show="configViewerJson" 
                              v-if="data.item.status_install && data.item.audit">

                    <template #title>Auditoría</template>                    
                    <b>Estado:</b> {{JSON.parse(data.item.audit).status}}<br>
                    <b>Auditoría:</b> <json-tree v-if="JSON.parse(data.item.audit).audit" :data="JSON.parse(JSON.parse(data.item.audit).audit)" :level="1" class="intances-audit-pre-json"></json-tree><br>
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.audit).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>   
                </template>

                <template v-slot:cell(access)="data">
                  <div v-if="data.item.status_install && data.item.access">
                    <div v-if="showFieldSystem(data.item, 'access') == 'Activo'" class="text-success font-weight-bold" :id="'server-access-' + data.item.id">
                      <b-icon icon="circle-fill" variant="success"></b-icon>
                    </div>
                    <div v-if="showFieldSystem(data.item, 'access') == 'Inactivo'" class="text-danger font-weight-bold" :id="'server-access-' + data.item.id">
                      <b-icon icon="circle-fill" variant="danger"></b-icon>
                    </div>   
                  </div>

                  <b-popover :target="'server-access-' + data.item.id" 
                              triggers="hover" 
                              placement="top"
                              @show="configViewerJson" 
                              v-if="data.item.status_install && data.item.access">

                    <template #title>Accesos URL</template>                    
                    <b>Estado:</b> {{JSON.parse(data.item.access).status}}<br>
                    <div v-if="JSON.parse(data.item.access).domain">
                      <b>Sistema:</b> {{JSON.parse(data.item.access).domain}}<br>
                    </div>
                    <div v-if="JSON.parse(data.item.access).domain_alt">
                      <b>Sistema Alternativo:</b> {{JSON.parse(data.item.access).domain_alt}}<br>
                    </div>                    
                    <div v-if="JSON.parse(data.item.access).domain_site">
                      <b>Sitio Web:</b> {{JSON.parse(data.item.access).domain_site}}<br>
                    </div>
                    <div v-if="JSON.parse(data.item.access).domain_site_alt">
                      <b>Sitio Web Alternativo:</b> {{JSON.parse(data.item.access).domain_site_alt}}<br>
                    </div>                    
                    <b>Última Sincronización:</b> {{moment(JSON.parse(data.item.access).datetime).format('DD/MM/YYYY HH:mm:ss')}}
                  </b-popover>   
                </template>

                <template v-slot:cell(dns)="data">
                  <div v-if="data.item.status_install && data.item.domain && data.item.access">
                    <b-icon v-if="showDnsTypeAAAA(data.item)" icon="info-circle-fill" class="mr-1" variant="info" title="Encontramos registros tipo AAAA en los DNS"></b-icon>
                    <b-icon v-if="showDnsErrorIp(data.item)" icon="folder-symlink-fill" variant="info" title="La IP asociada al registro tipo A no coincide con la IP del servidor y no responde como Zyncro"></b-icon>
                    <b-icon v-if="showDnsProxy(data.item)" icon="hdd-network-fill" class="mr-1" style="color:#8b5cf6; cursor:pointer;" v-b-tooltip.hover :title="getDnsProxyTitle(data.item)"></b-icon>
                  </div>
                </template>

                <template v-slot:cell(f_action)="data">
                  <b-dropdown right text="Acción" size="sm" variant="outline-dark" class="pull-right">                    
                    <b-dropdown-item @click="blockedSystem(data.item)" v-if="!data.item.blocked">
                      <b-icon icon="lock-fill"></b-icon> Bloquear
                    </b-dropdown-item>         
                    <b-dropdown-item @click="unlockedSystem(data.item)" v-else>
                      <b-icon icon="lock-fill"></b-icon> Desbloquear
                    </b-dropdown-item>         

                    <b-dropdown-header>Consola</b-dropdown-header>                    
                    <b-dropdown-item @click="actionSystem(data.item)">
                      <b-icon icon="terminal-fill"></b-icon> Comandos
                    </b-dropdown-item>         
                    
                    <b-dropdown-header>Acciones</b-dropdown-header>
                    <b-dropdown-item @click="edit(data.item)">
                      <i class="fa fa-pencil ml-0 mr-0" style="color:blue"></i> Editar
                    </b-dropdown-item>
                    <b-dropdown-item @click="remove(data.item)">
                      <i class="fa fa-trash mr-0 ml-0" style="color:red"></i> Eliminar
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
            </b-table>
            <EmptyState v-if="!table.items.length && !table.isBusy" icon="deployed_code" title="No se encontraron sistemas" />
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <div>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <AppIcon name="arrow_back" class="mr-1" /> Volver
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD MAIN -->
      <b-modal v-model="modal.form.active"
              :key="modal.form._id"
              :id="modal.form._id">                  
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="deployed_code" /></span>
          <h5 class="app-modal-header__title">{{modal.form.title}}</h5>
        </div>

        <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
          <b-tab>
            <template #title><AppIcon name="description" class="mr-1" /> General</template>
            <b-row>
              <b-col md="12">
                <b-form-group label="Nombre">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.form.name"
                                required
                                placeholder="Ingresar un nombre"
                                :disabled="crud.form.id>0">
                  </b-form-input>
                </b-form-group>
              </b-col>           
              <b-col md="12">
                <FindObject render="search"
                            type="customers" 
                            @select-object="loadCustomers($event)" 
                            :valueID="crud.form.customer_id"
                            :where="conditionCustomersActive" />           
              </b-col>
              <b-col md="12">
                <FindObject tag="VPS Instalación"
                            render="search"
                            type="instances-server"
                            @select-object="loadInstancesServer($event)"
                            :valueID="crud.form.instances_server_id"
                            :where="conditionServerType" />
              </b-col>
              <b-col md="12">
                <FindObject tag="Agrupación"
                            render="search"
                            type="instances-categories"
                            @select-object="loadInstancesCategories($event)"
                            :valueID="crud.form.categories_id"
                            :where="filterCategoriesSystem" />
              </b-col>

              <b-col md="12">
                <hr>
              </b-col> 

              <b-col md="12" v-if="parameters.integrateServices">
                <FindObject tag="Servicio"              
                            render="search"
                            display="name"
                            type="servicesCustomers" 
                            @select-object="loadServicesCustomersDetails($event)" 
                            :valueID="crud.form.services_customers_details_id" 
                            :where="conditionServicesCustomersDetails" 
                            :key="crud.form.customer_id"/>           
              </b-col>              

              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea                
                    v-model="crud.form.observations"
                    lazy
                    placeholder="Ingresar las observaciones..."
                    rows="3"
                    max-rows="6"
                  ></app-textarea>
                </b-form-group>
              </b-col>       

              <b-col md="12" v-if="!crud.form.id">
                <hr>
              </b-col>    
              <b-col md="6" v-if="!crud.form.id">
                <b-form-checkbox v-model="crud.form.status_system" 
                                  switch 
                                  size="sm" 
                                  class="pull-left">
                  ¿Sistema ya instalado?
                </b-form-checkbox> 
              </b-col>  
              <b-col md="6" v-if="!crud.form.id">
                <b-form-checkbox v-model="crud.form.status_site" 
                                  switch 
                                  size="sm" 
                                  class="pull-left">
                  ¿Sitio Web ya instalado?
                </b-form-checkbox> 
              </b-col> 
              <b-col md="12">
                <hr>
              </b-col>   
              <b-col md="12" class="mb-4">
                <b-form-checkbox v-model="crud.form.autocontrol" 
                                  switch 
                                  size="sm" 
                                  class="pull-left">
                  ¿Es el sistema principal? <small>Le llamamos sistema principal al padre que controla todos los sistemas hijos</small>
                </b-form-checkbox> 
              </b-col>                                              
            </b-row>
          </b-tab>  
          <b-tab>
            <template #title><AppIcon name="backup" class="mr-1" /> Backups</template>
            <b-row>
              <b-col md="12">
                <b-form-checkbox v-model="crud.form.control_backups" 
                                  switch 
                                  size="sm" 
                                  class="pull-left mt-2 mb-2">
                  ¿Hacer backups diariamente?
                </b-form-checkbox> 
              </b-col>            
              <b-col md="12" v-if="crud.form.control_backups">
                <FindObject tag="VPS Backups"
                            render="search"
                            type="instances-server" 
                            @select-object="loadInstancesServerBackups($event)" 
                            :valueID="crud.form.instances_server_backups_id" 
                            :where="conditionServerBackupsType" />           
              </b-col>    
              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea                
                    v-model="crud.form.observations_backups"
                    lazy
                    placeholder="Ingresar las observaciones sobre el backups..."
                    rows="3"
                    max-rows="6"
                  ></app-textarea>
                </b-form-group>
              </b-col>                 
            </b-row>            
          </b-tab>
        </b-tabs>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" /> Guardar</b-button>
        </div>
      </b-modal>

      <!-- CRUD COMMAND -->
      <b-modal v-model="modal.command.active"
              size="lg"
              :key="modal.command._id"
              :id="modal.command._id">                  
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="terminal" /></span>
          <h5 class="app-modal-header__title">{{modal.command.title}}</h5>
        </div>

        <b-row v-if="selectedSystem">
          <b-col md="3" class="instances-system-command-panel-column">
            <aside class="instances-system-command-panel" aria-label="Comandos del sistema">
              <h6><AppIcon name="terminal" /> Comandos</h6>
              <section class="instances-system-command-group">
                <b-button v-b-toggle.system-command-system variant="link" class="instances-system-command-group__toggle"><span><AppIcon name="deployed_code" /> Sistema</span><AppIcon name="expand_more" /></b-button>
                <b-collapse id="system-command-system" visible accordion="system-commands"><div class="instances-system-command-group__actions">
                  <span v-if="selectedSystem.status_install" class="instances-system-command-subsection">Consulta</span>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Muestra la contraseña actual de los usuarios superadmin y admin del sistema." variant="link" class="instances-system-command-action" @click="getActionSystem('system-get-password','Obtener Contraseña (superadmin/admin)')"><AppIcon name="key" /> Contraseña</b-button>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Muestra la versión de código actualmente instalada en el sistema." variant="link" class="instances-system-command-action" @click="getActionSystem('system-get-version','Obtener Versión')"><AppIcon name="info" /> Versión</b-button>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Descarga las últimas líneas del log de errores del sistema." variant="link" class="instances-system-command-action" @click="getActionSystem('system-get-last-log-error','Obtener Últimos Errores')"><AppIcon name="description" /> Últimos errores</b-button>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Genera y trae el archivo de auditoría del sistema (clientes, proveedores, staff, logins, recordatorios, tareas, etc.)." variant="link" class="instances-system-command-action" @click="getActionSystem('system-audit','Auditoria del sistema')"><AppIcon name="fact_check" /> Auditar</b-button>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Verifica que los dominios configurados del sistema respondan correctamente." variant="link" class="instances-system-command-action" @click="getActionSystem('system-check-access','Verificar Acceso')"><AppIcon name="link" /> Verificar acceso</b-button>
                  <span class="instances-system-command-subsection">Administración</span>
                  <b-button v-if="selectedSystem.status_install && !selectedSystem.status_disable" v-b-tooltip.hover.righttop.window title="Configura el dominio personalizado con el que se accede al sistema." variant="link" class="instances-system-command-action" @click="getActionSystem('system-domain','Apuntar Dominio')"><AppIcon name="language" /> Apuntar dominio</b-button>
                  <b-button v-if="selectedSystem.status_install && !selectedSystem.status_disable && selectedSystem.domain" v-b-tooltip.hover.righttop.window title="Genera el certificado SSL (Let's Encrypt) para el dominio del sistema." variant="link" class="instances-system-command-action" @click="getActionSystem('system-ssl-generate','Generar SSL')"><AppIcon name="verified_user" /> Generar SSL</b-button>
                  <b-button v-if="!selectedSystem.status_disable && selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Deshabilita el acceso al sistema sin desinstalarlo." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-disable','Deshabilitar Sistema')"><AppIcon name="pause_circle" /> Deshabilitar</b-button>
                  <b-button v-if="selectedSystem.status_disable && selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Vuelve a habilitar el acceso a un sistema deshabilitado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-enable','Habilitar Sistema')"><AppIcon name="play_circle" /> Habilitar</b-button>
                  <b-button v-if="!selectedSystem.status_install && !selectedSystem.blocked" v-b-tooltip.hover.righttop.window title="Instala el sistema desde cero en el servidor asignado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-install','Instalar Sistema')"><AppIcon name="download" /> Instalar</b-button>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Actualiza el sistema a la última versión de código disponible." variant="link" class="instances-system-command-action" @click="getActionSystem('system-update','Actualizar Sistema')"><AppIcon name="upgrade" /> Actualizar</b-button>
                  <b-button v-if="selectedSystem.status_install && !selectedSystem.status_install_site && !selectedSystem.blocked" v-b-tooltip.hover.righttop.window title="Elimina el sistema instalado del servidor. Acción irreversible." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-uninstall','Desinstalar Sistema')"><AppIcon name="delete" /> Desinstalar</b-button>
                  <b-button v-if="selectedSystem.status_install" v-b-tooltip.hover.righttop.window title="Vacía el log de errores del sistema." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-clean-last-log-error','Limpiar Últimos Errores')"><AppIcon name="cleaning_services" /> Limpiar errores</b-button>
                </div></b-collapse>
              </section>
              <section v-if="showSiteWebActionsDropdown()" class="instances-system-command-group">
                <b-button v-b-toggle.system-command-site variant="link" class="instances-system-command-group__toggle"><span><AppIcon name="language" /> Sitio web</span><AppIcon name="expand_more" /></b-button>
                <b-collapse id="system-command-site" accordion="system-commands"><div class="instances-system-command-group__actions">
                  <span v-if="selectedSystem.status_install_site" class="instances-system-command-subsection">Consulta</span>
                  <b-button v-if="selectedSystem.status_install_site" v-b-tooltip.hover.righttop.window title="Muestra la versión de código actualmente instalada en el sitio web." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-get-version','Obtener Versión')"><AppIcon name="info" /> Versión</b-button>
                  <b-button v-if="selectedSystem.status_install_site" v-b-tooltip.hover.righttop.window title="Descarga las últimas líneas del log de errores del sitio web." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-get-log-error','Obtener Errores')"><AppIcon name="description" /> Errores</b-button>
                  <span class="instances-system-command-subsection">Administración</span>
                  <b-button v-if="selectedSystem.status_install && selectedSystem.status_install_site && !selectedSystem.status_disable_site" v-b-tooltip.hover.righttop.window title="Configura el dominio personalizado del sitio web." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-domain','Apuntar Dominio')"><AppIcon name="language" /> Apuntar dominio</b-button>
                  <b-button v-if="selectedSystem.status_install && selectedSystem.status_install_site && !selectedSystem.status_disable_site && selectedSystem.domain_site" v-b-tooltip.hover.righttop.window title="Genera el certificado SSL (Let's Encrypt) para el dominio del sitio web." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-ssl-generate','Generar SSL')"><AppIcon name="verified_user" /> Generar SSL</b-button>
                  <b-button v-if="!selectedSystem.status_disable_site && selectedSystem.status_install_site" v-b-tooltip.hover.righttop.window title="Deshabilita el acceso al sitio web sin desinstalarlo." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-web-disable','Deshabilitar Sitio Web')"><AppIcon name="pause_circle" /> Deshabilitar</b-button>
                  <b-button v-if="selectedSystem.status_disable_site && selectedSystem.status_install_site" v-b-tooltip.hover.righttop.window title="Vuelve a habilitar el acceso a un sitio web deshabilitado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-enable','Habilitar Sitio Web')"><AppIcon name="play_circle" /> Habilitar</b-button>
                  <b-button v-if="!selectedSystem.status_install_site && !selectedSystem.blocked" v-b-tooltip.hover.righttop.window title="Instala el sitio web desde cero en el servidor asignado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-install','Instalar Sitio Web')"><AppIcon name="download" /> Instalar</b-button>
                  <b-button v-if="selectedSystem.status_install_site" v-b-tooltip.hover.righttop.window title="Actualiza el sitio web a la última versión de código disponible." variant="link" class="instances-system-command-action" @click="getActionSystem('system-web-update','Actualizar Sitio Web')"><AppIcon name="upgrade" /> Actualizar</b-button>
                  <b-button v-if="selectedSystem.status_install_site && !selectedSystem.blocked" v-b-tooltip.hover.righttop.window title="Elimina el sitio web instalado del servidor. Acción irreversible." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-web-uninstall','Desinstalar Sitio Web')"><AppIcon name="delete" /> Desinstalar</b-button>
                  <b-button v-if="selectedSystem.status_install_site" v-b-tooltip.hover.righttop.window title="Vacía el log de errores del sitio web." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-web-clean-log-error','Limpiar Errores')"><AppIcon name="cleaning_services" /> Limpiar errores</b-button>
                </div></b-collapse>
              </section>
              <section v-if="selectedSystem.status_install && !selectedSystem.blocked" class="instances-system-command-group">
                <b-button v-b-toggle.system-command-products variant="link" class="instances-system-command-group__toggle"><span><AppIcon name="inventory_2" /> Productos</span><AppIcon name="expand_more" /></b-button>
                <b-collapse id="system-command-products" accordion="system-commands"><div class="instances-system-command-group__actions">
                  <b-button variant="link" v-b-tooltip.hover.righttop.window title="Instala el paquete de productos precargados del módulo Comercial." class="instances-system-command-action" @click="getActionSystem('system-install-products-z-comercial','Comercial')"><AppIcon name="add" /> Comercial</b-button>
                  <b-button variant="link" v-b-tooltip.hover.righttop.window title="Instala el paquete de productos precargados del módulo Pedidos." class="instances-system-command-action" @click="getActionSystem('system-install-products-z-pedidos','Pedidos')"><AppIcon name="add" /> Pedidos</b-button>
                  <b-button variant="link" v-b-tooltip.hover.righttop.window title="Instala el paquete combinado de productos precargados de Comercial y Pedidos." class="instances-system-command-action" @click="getActionSystem('system-install-products-z-comercial-pedidos','Comercial + Pedidos')"><AppIcon name="add" /> Comercial + Pedidos</b-button>
                  <b-button variant="link" v-b-tooltip.hover.righttop.window title="Instala el paquete de productos precargados del módulo Ventas." class="instances-system-command-action" @click="getActionSystem('system-install-products-z-ventas','Ventas')"><AppIcon name="add" /> Ventas</b-button>
                </div></b-collapse>
              </section>
              <section v-if="selectedSystem.status_install && selectedSystem.control_backups" class="instances-system-command-group">
                <b-button v-b-toggle.system-command-backups variant="link" class="instances-system-command-group__toggle"><span><AppIcon name="backup" /> Backups</span><AppIcon name="expand_more" /></b-button>
                <b-collapse id="system-command-backups" accordion="system-commands"><div class="instances-system-command-group__actions">
                  <span v-if="selectedSystem.backups" class="instances-system-command-subsection">Consulta</span>
                  <b-button v-if="selectedSystem.backups" v-b-tooltip.hover.righttop.window title="Muestra si el backup programado de hoy ya se generó." variant="link" class="instances-system-command-action" @click="getActionSystem('system-backups-get-now','Backups Hoy')"><AppIcon name="backup" /> Backups hoy</b-button>
                  <b-button v-if="selectedSystem.backups" v-b-tooltip.hover.righttop.window title="Lista los backups de base de datos disponibles en el servidor de backups." variant="link" class="instances-system-command-action" @click="getActionSystem('system-backups-list-db','Listar Bases de datos')"><AppIcon name="database" /> Bases de datos</b-button>
                  <b-button v-if="selectedSystem.backups" v-b-tooltip.hover.righttop.window title="Lista los backups de imágenes/archivos disponibles en el servidor de backups." variant="link" class="instances-system-command-action" @click="getActionSystem('system-backups-list-img','Listar Imágenes')"><AppIcon name="image" /> Imágenes</b-button>
                  <span v-if="selectedSystem.backups" class="instances-system-command-subsection">Forzar</span>
                  <b-button v-if="selectedSystem.backups" v-b-tooltip.hover.righttop.window title="Fuerza la generación de un backup de archivos ahora, sin esperar al horario programado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-backups-force-files','Forzar Backups: FILES')"><AppIcon name="backup" /> Archivos</b-button>
                  <b-button v-if="selectedSystem.backups" v-b-tooltip.hover.righttop.window title="Fuerza la generación de un backup de base de datos ahora, sin esperar al horario programado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-backups-force-db','Forzar Backups: DB')"><AppIcon name="backup" /> Base de datos</b-button>
                  <span class="instances-system-command-subsection">Administración</span>
                  <b-button v-if="!showConfigurateBackupsActive()" v-b-tooltip.hover.righttop.window title="Activa y configura los backups automáticos diarios en el servidor de backups asignado." variant="link" class="instances-system-command-action" @click="getActionSystem('system-backups-configurate','Configurar Backups')"><AppIcon name="settings" /> Configurar</b-button>
                  <b-button v-if="showConfigurateBackupsActive()" v-b-tooltip.hover.righttop.window title="Desactiva y quita la configuración de backups automáticos del servidor de backups." variant="link" class="instances-system-command-action instances-system-command-action--danger" @click="getActionSystem('system-backups-delete','Quitar Backups')"><AppIcon name="delete" /> Quitar</b-button>
                </div></b-collapse>
              </section>
            </aside>
          </b-col>
          <b-col md="9" class="app-command-terminal-column">
            <section class="app-command-terminal">
              <div class="app-command-terminal__header">
                <h4>{{this.modal.command.subtitle}}</h4>
                <div class="app-command-terminal__actions">
                <span v-if="selectedSystem.status_process">
                  <span class="app-command-terminal__process"><AppIcon name="progress_activity" /> Procesando</span>
                </span>
                <b-button v-if="!SSHLoader && SSHConsole" variant="outline-secondary" size="sm" class="app-command-terminal__copy" @click="copySSHConsole()">
                  <AppIcon name="content_copy" class="mr-1" /> Copiar
                </b-button>
                <b-button variant="danger" size="sm" class="app-command-terminal__refresh" title="Refrescar" @click="refreshSystem()" v-if="!SSHLoader">
                  <AppIcon name="refresh" label="Refrescar" />
                </b-button>
                </div>
              </div>
              <div v-if="!SSHLoader" ref="sshConsole" class="ssh-console-system" v-html="SSHConsole"></div>
              <div v-else class="app-command-terminal__loader">
              <b-spinner label="Spinning" type="grow"></b-spinner>
              </div>
            </section>
          </b-col>
        </b-row>

        <div slot="modal-footer" class="ssh-console-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="closeActionSystem()"><AppIcon name="x" class="mr-1" /> Cerrar</b-button>
        </div>
      </b-modal>     

      <!-- CRUD DOMAIN SYSTEM -->
      <b-modal v-model="modal.formSystem.active"
              size="xl">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="language" /></span>
          <h5 class="app-modal-header__title">{{modal.formSystem.title}}</h5>
        </div>

        <b-row>
          <b-col md="6">
            <b-row>
              <b-col md="12">
                <b-form-group label="Domino">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.formSystem.domain"
                                required
                                placeholder="Ingresa un dominio">
                  </b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-alert variant="info" show class="d-flex align-items-start">
                  <AppIcon name="info" class="mr-2 mt-1" />
                  <span>Puede demorar unas horas en quedar activo. Dependerá de la propagación de los DNS de su proveedor de dominios.</span>
                </b-alert>
              </b-col>
            </b-row>
          </b-col>                                       
          <b-col md="6" v-if="this.selectedSystem">
            <section class="app-modal-domain-guide">
              <div class="app-modal-domain-guide__title"><AppIcon name="dns" class="mr-1" /> Implementación DNS</div>
              <p>Para apuntar su dominio personalizado al sistema, siga estos pasos:</p>
              <ol class="mb-0 pl-3">
                <li>Ingresar a la <b>zona DNS</b> de su dominio.</li>
                <li>Buscar el registro <b>Tipo A → su-dominio.com → [IP]</b>.</li>
                <li>Editar la IP actual por <b>{{this.selectedSystem.server.host}}</b>.</li>
                <li>Mantener el registro <b>CNAME → www.su-dominio.com → su-dominio.com</b>.</li>
              </ol>
            </section>
          </b-col>          
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formSystem.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="saveDomain('system')"><AppIcon name="link" class="mr-1" /> Apuntar</b-button>
        </div>
      </b-modal> 

      <!-- CRUD DOMAIN SITE -->
      <b-modal v-model="modal.formSite.active"
              size="xl">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="language" /></span>
          <h5 class="app-modal-header__title">{{modal.formSite.title}}</h5>
        </div>

        <b-row>
          <b-col md="6">
            <b-row>
              <b-col md="12">
                <b-form-group label="Domino">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.formSite.domain"
                                required
                                placeholder="Ingresa un dominio">
                  </b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-alert variant="info" show class="d-flex align-items-start">
                  <AppIcon name="info" class="mr-2 mt-1" />
                  <span>Puede demorar unas horas en quedar activo. Dependerá de la propagación de los DNS de su proveedor de dominios.</span>
                </b-alert>
              </b-col>
            </b-row>
          </b-col>                                       
          <b-col md="6" v-if="this.selectedSystem">
            <section class="app-modal-domain-guide">
              <div class="app-modal-domain-guide__title"><AppIcon name="dns" class="mr-1" /> Implementación DNS</div>
              <p>Para apuntar su dominio personalizado al sistema, siga estos pasos:</p>
              <ol class="mb-0 pl-3">
                <li>Ingresar a la <b>zona DNS</b> de su dominio.</li>
                <li>Buscar el registro <b>Tipo A → su-dominio.com → [IP]</b>.</li>
                <li>Editar la IP actual por <b>{{this.selectedSystem.server.host}}</b>.</li>
                <li>Mantener el registro <b>CNAME → www.su-dominio.com → su-dominio.com</b>.</li>
              </ol>
            </section>
          </b-col>          
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formSite.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="saveDomain('site')"><AppIcon name="link" class="mr-1" /> Apuntar</b-button>
        </div>
      </b-modal>             
    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import Param from '@/config/parameters'
  import FindObject from '@/components/inc/find/findObject'  
  import Storage from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  import Vue from 'vue'
  import JsonTree from 'vue-json-tree'
  Vue.component('json-tree', JsonTree)

  export default {
    components: {
      FindObject,      
      TabHeaderActions,
    },        
    data: () => {
      return {      
        access: {
          module_id: Modules.INSTANCIAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudInstancesSystem',
          elements: {}
        },       
        parameters: {
          integrateServices: Helper.hasParametersAccess(Param.P59),          
        },              
        table : {
          items: [],
          fields: [],          
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,                
        },
        filtersOpen: false,
        systemViewMode: 'tree',
        expandedSystemGroups: {},
        crud: {
          form: {
            id: 0,
            name: '',
            customer_id: 0,
            customer: null,
            instances_server_id: 0,
            instances_server: null,
            instances_server_backups_id: 0,
            instances_server_backups: null,
            services_customers_details_id: 0,
            services_customers_details: null,
            categories_id: 0,
            categories: null,
            observations: '',
            own: true,
            type: 'zyncro',
            status_system: false,
            status_site: false,
            autocontrol: false,
            control_backups: false,
            observations_backups: '',
          },     
          formSystem: {
            id: 0,
            domain: '',
          },     
          formSite: {
            id: 0,
            domain: '',
          },               
        },
        modal: {
          form: {
            active: false,
            title: ''
          },
          command: {
            active: false,
            title: '',
            subtitle: 'Terminal',
          },       
          formSystem: {
            active: false,
            title: ''            
          },   
          formSite: {
            active: false,
            title: ''            
          }             
        },
        filters: {
          name: '',
          customer: 0,
          instances_server: 0,
          categories: null,
          own: true,
          type: 'zyncro',
          health: { code: 'all', label: 'Todos' },
        },
        filterType: 'system',
        arr: {
          select: {
            customers: [],
            instances_servers: [],
            categories: [],
          },
          filter: {
            health: [
              { code: 'all', label: 'Todos' },
              { code: 'alerts', label: 'Con alertas operativas' },
              { code: 'without_backups', label: 'Sin backups activos' },
              { code: 'access_problems', label: 'Con problemas de acceso' },
              { code: 'dns_problems', label: 'Con alertas DNS' },
              { code: 'unblocked', label: 'Sistemas desbloqueados' },
            ]
          }
        },       
        selectedServer: null,           
        selectedSystem: null,
        SSHConsole: '',
        SSHLoader: false,    
      }
    },
    created () {     
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },  
    mounted() {
      this.filterLoadSelects()
      this.restoreFilterStorage() 
      this.filtersOpen = Storage.getValue('filters_open_crud_instances_system') == 'true'
      var storedSystemViewMode = Storage.getValue('view_mode_crud_instances_system')
      this.systemViewMode = ['cards', 'full'].indexOf(storedSystemViewMode) >= 0 ? storedSystemViewMode : 'tree'

      this.loadFieldTable()

      this.filter()

      this.configViewerJson()
    },
    watch: {
      filtersOpen(value) {
        Storage.setValue('filters_open_crud_instances_system', value ? 'true' : 'false')
      },
      systemViewMode(value) {
        Storage.setValue('view_mode_crud_instances_system', value)
      }
    },
    computed: {
      groupedSystems() {
        var groups = {}
        this.table.items.forEach(item => {
          var category = item.categorie || { id: 'uncategorized', name: 'Sin categoría' }
          var key = category.id || 'uncategorized'
          if(!groups[key]) {
            groups[key] = { id: key, name: category.name, items: [] }
          }
          groups[key].items.push(item)
        })
        return Object.values(groups)
          .map(group => {
            group.health = this.getSystemGroupHealth(group.items)
            return group
          })
          .sort((first, second) => this.getSystemGroupSeverity(second.health) - this.getSystemGroupSeverity(first.health)
            || this.getSystemGroupPriority(second.health) - this.getSystemGroupPriority(first.health)
            || first.name.localeCompare(second.name))
      },
      // CONDITIONS SELECT
      filterCategoriesSystem() {
        return [{field: 'type', condition: this.filterType}]
      },
      conditionCustomersActive(){
        return [{field: 'active', condition: true}];
      },  
      conditionServerType(){
        return [{field: 'type', condition: 'fullcontrol'}];
      },  
      conditionServerBackupsType(){
        return [{field: 'type', condition: 'backups'}];
      },         
      conditionServicesCustomersDetails() {
        return [
          {field: 'customer_id', condition: this.crud.form.customer_id},
          {field: 'canceled', condition: false},
          {field: 'type_instances', condition: 'system'},          
        ];
      }
    },    
    methods: {
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])

        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle || 'Confirmar',
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      isSystemGroupOpen(id) {
        if(this.systemViewMode == 'full') return true
        return !!this.expandedSystemGroups[id]
      },
      toggleSystemGroup(id) {
        if(this.systemViewMode == 'full') return
        this.$set(this.expandedSystemGroups, id, !this.isSystemGroupOpen(id))
      },
      syncSystemGroupsExpansion(items) {
        var groupIds = {}
        items.forEach(item => {
          var category = item.categorie || { id: 'uncategorized' }
          groupIds[category.id || 'uncategorized'] = true
        })
        var groupKeys = Object.keys(groupIds)
        var hasPreviousState = Object.keys(this.expandedSystemGroups).length > 0
        var expandedGroups = {}
        groupKeys.forEach(key => {
          expandedGroups[key] = hasPreviousState ? !!this.expandedSystemGroups[key] : groupKeys.length == 1
        })
        this.expandedSystemGroups = expandedGroups
      },
      getSystemUrl(item) {
        if(!item.status_install) return ''
        if(item.domain) return 'http://' + item.domain
        if(item.server && item.server.subdomain) return 'http://' + item.name + '.' + item.server.subdomain
        return ''
      },
      getSiteUrl(item) {
        if(!item.status_install_site) return ''
        if(item.domain_site) return 'http://' + item.domain_site
        if(item.server && item.server.subdomain) return 'http://site.' + item.name + '.' + item.server.subdomain
        return ''
      },
      getSystemInstallLabel(item) {
        if(!item.status_install) return 'No instalado'
        return item.status_disable ? 'Deshabilitado' : 'Instalado'
      },
      getSystemInstallVariant(item) {
        if(!item.status_install) return 'danger'
        return item.status_disable ? 'warning' : 'success'
      },
      getSystemInstallIcon(item) {
        if(!item.status_install) return 'close'
        return item.status_disable ? 'pause_circle' : 'check_circle'
      },
      getSiteInstallLabel(item) {
        if(!item.status_install_site) return 'No instalado'
        return item.status_disable_site ? 'Deshabilitado' : 'Instalado'
      },
      getSiteInstallVariant(item) {
        if(!item.status_install_site) return 'secondary'
        return item.status_disable_site ? 'warning' : 'success'
      },
      getSiteInstallIcon(item) {
        if(!item.status_install_site) return 'close'
        return item.status_disable_site ? 'pause_circle' : 'check_circle'
      },
      getSystemDetails(item, key) {
        try {
          return item[key] ? JSON.parse(item[key]) : {}
        } catch (error) {
          return {}
        }
      },
      getSystemAuditData(item) {
        try {
          var audit = this.getSystemDetails(item, 'audit').audit
          return audit ? JSON.parse(audit) : null
        } catch (error) {
          return null
        }
      },
      getSystemChecks(item) {
        var checks = [
          { key: 'backups', label: 'Backups', icon: 'backup', title: 'Estado de backups' },
          { key: 'audit', label: 'Auditoría', icon: 'fact_check', title: 'Estado de auditoría' },
          { key: 'access', label: 'Accesos', icon: 'link', title: 'Estado de accesos URL' },
        ].filter(check => item[check.key])
        if(item.status_install && item.access) {
          checks.push(this.showDnsProxy(item)
            ? { key: 'dns', label: 'Proxy', icon: 'lan', title: 'Proxy Zyncro' }
            : { key: 'dns', label: 'DNS', icon: 'language', title: 'Estado de DNS' })
        }
        return checks
      },
      getSystemCheckStatus(item, key) {
        if(key == 'dns') return this.getDnsStatus(item)
        return this.getSystemDetails(item, key).status || 'Sin estado'
      },
      getSystemCheckTone(item, key) {
        if(key == 'dns') return this.hasSystemDnsProblem(item) ? 'text-danger' : 'text-success'
        return this.getSystemCheckStatus(item, key) == 'Activo' ? 'text-success' : 'text-danger'
      },
      isSystemWithoutBackups(item) {
        return item.status_install && this.getSystemCheckStatus(item, 'backups') != 'Activo'
      },
      hasSystemAccessProblem(item) {
        return item.status_install && item.access && this.getSystemCheckStatus(item, 'access') != 'Activo'
      },
      hasSystemDnsProblem(item) {
        return item.status_install && (this.showDnsTypeAAAA(item) || this.showDnsErrorIp(item))
      },
      matchesSelectedHealth(item) {
        var health = this.filters.health ? this.filters.health.code : 'all'
        if(health == 'without_backups') return this.isSystemWithoutBackups(item)
        if(health == 'access_problems') return this.hasSystemAccessProblem(item)
        if(health == 'dns_problems') return this.hasSystemDnsProblem(item)
        if(health == 'unblocked') return !item.blocked
        if(health == 'alerts') {
          return this.isSystemWithoutBackups(item)
            || this.hasSystemAccessProblem(item)
            || this.hasSystemDnsProblem(item)
            || !item.blocked
        }

        return true
      },
      getDnsStatus(item) {
        if(this.showDnsErrorIp(item)) return 'Error de IP'
        if(this.showDnsTypeAAAA(item)) return 'Registro AAAA'
        if(this.showDnsProxy(item)) return 'Zyncro'
        return 'Correcto'
      },
      getDnsStatusDetail(item) {
        if(this.showDnsErrorIp(item)) return 'La IP del registro A no coincide con la IP del servidor.'
        if(this.showDnsTypeAAAA(item)) return 'Se detectaron registros AAAA en los DNS.'
        if(this.showDnsProxy(item)) return this.getDnsProxyTitle(item)
        return 'No se detectaron alertas DNS.'
      },
      getSystemGroupHealth(items) {
        var health = { withoutBackups: 0, accessProblems: 0, dnsProblems: 0, unblocked: 0, processing: 0 }
        items.forEach(item => {
          if(this.isSystemWithoutBackups(item)) health.withoutBackups++
          if(this.hasSystemAccessProblem(item)) health.accessProblems++
          if(this.hasSystemDnsProblem(item)) health.dnsProblems++
          if(!item.blocked) health.unblocked++
          if(item.status_process) health.processing++
        })
        return health
      },
      getSystemGroupPriority(health) {
        return health.withoutBackups + health.accessProblems + health.dnsProblems + health.unblocked + health.processing
      },
      getSystemGroupSeverity(health) {
        if(health.accessProblems || health.dnsProblems) return 3
        if(health.withoutBackups) return 2
        if(health.unblocked) return 1
        return 0
      },
      getSystemGroupHealthLabel(health) {
        var alerts = []
        if(health.withoutBackups) alerts.push(health.withoutBackups + ' sin backups')
        if(health.accessProblems) alerts.push(health.accessProblems + ' con problemas de acceso')
        if(health.dnsProblems) alerts.push(health.dnsProblems + ' con alertas DNS')
        if(health.unblocked) alerts.push(health.unblocked + ' desbloqueados')
        if(health.processing) alerts.push(health.processing + ' procesando')
        return alerts.length ? 'Alertas del grupo: ' + alerts.join(', ') : 'Grupo saludable'
      },
      // config
      configViewerJson() {    
        setTimeout(()=>{
          var arrElement = document.getElementsByClassName("json-tree-indent");        
          if(arrElement) {
            for (var i = 0; i < arrElement.length; i++){
              if(arrElement[i].innerHTML != '&nbsp;') {
                arrElement[i].innerHTML = '&nbsp;';            
              }                        
            }                              
          }
        },100)                    
      },  
      getAccessInfo(item) {
        if(!item.access) {
          return null
        }

        try {
          return JSON.parse(item.access)
        } catch (error) {
          return null
        }
      },
      isDnsDirectIp(item) {
        var access = this.getAccessInfo(item)
        var serverHost = item.server ? item.server.host : ''

        return access && access.dns_get_ip && serverHost && access.dns_get_ip == serverHost
      },
      isDnsCheckZyncro(item) {
        var access = this.getAccessInfo(item)

        return access && (access.dns_check_zyncro === true || access.dns_check_zyncro === 1 || access.dns_check_zyncro === '1' || access.dns_check_zyncro === 'true')
      },
      showDnsTypeAAAA(item) {
        var access = this.getAccessInfo(item)

        return access && access.dns_type_aaaa && this.isDnsDirectIp(item)
      },
      showDnsErrorIp(item) {
        var access = this.getAccessInfo(item)
        var serverHost = item.server ? item.server.host : ''

        return access && access.dns_get_ip && serverHost && access.dns_get_ip != serverHost && !this.isDnsCheckZyncro(item)
      },
      showDnsProxy(item) {
        return this.isDnsCheckZyncro(item)
      },
      getDnsProxyTitle(item) {
        var access = this.getAccessInfo(item)

        if(!access) {
          return ''
        }

        var path = access.dns_check_zyncro_path || '/backend/public/storage/check-zyncro'
        var url = access.dns_check_zyncro_url || ''
        var content = access.dns_check_zyncro_content || 'true'

        return 'Proxy Zyncro detectado. Ruta: ' + path + '. URL: ' + url + '. Contenido: ' + content
      },
      loadFieldTable() {       
        this.table.fields.push({key: 'id', label: 'ID', sortable: true, class: 'align-middle'})
        
        if(this.parameters.integrateServices) {
          this.table.fields.push({key: 'services', label: '', class: 'align-middle'})                 
        }
        
        this.table.fields.push({key: 'name', label: 'Nombre', class: 'align-middle'})
        this.table.fields.push({key: 'customer_id', label: 'Cliente', class: 'align-middle'})
        this.table.fields.push({key: 'observations', label: '', class: 'align-middle'})
        this.table.fields.push({key: 'instances_server_id', label: 'VPS Instalación', class: 'align-middle'})
        this.table.fields.push({key: 'status_system', label: 'Sistema', class: 'align-middle text-center'})
        this.table.fields.push({key: 'status_site', label: 'Sitio Web', class: 'align-middle text-center'})
        this.table.fields.push({key: 'status_process', label: '', class: 'align-middle text-center'})
        this.table.fields.push({key: 'instances_server_backups_id', label: 'VPS Backups', class: 'align-middle'})
        this.table.fields.push({key: 'backups', label: 'Backups', class: 'align-middle text-center'})
        this.table.fields.push({key: 'audit', label: 'Auditoria', class: 'align-middle text-center'})
        this.table.fields.push({key: 'access', label: 'Accesos', class: 'align-middle text-center'})
        this.table.fields.push({key: 'dns', label: 'DNS', class: 'align-middle text-center'})
        this.table.fields.push({key: 'f_action', label:'', class: 'align-middle text-center'})
      },
      // crud
      filter() {
        this.saveFilterStorage()

        this.table.isBusy = true

        var result = serviceAPI.filtrarSistema(this.filters, this.table.currentPage)

        result.then((response) => {          
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data.filter(item => this.matchesSelectedHealth(item))
          if(this.filters.health && this.filters.health.code != 'all') {
            this.table.tablePaginate.total = this.table.items.length
            this.table.tablePaginate.last_page = 1
          }
          this.syncSystemGroupsExpansion(this.table.items)

          this.table.items.forEach(element => {            
            if(element.autocontrol) {
              element._rowVariant = 'dark'
            }            
          });

          if(this.$refs.table) {                        
            this.$refs.table.$forceUpdate()
          }  

          this.table.isBusy = false          
        })
        .catch(() => {
          this.table.isBusy = false
          //this.$awn.alert(Error.showError(error))
        });        
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.name = ''
        this.crud.form.customer_id = 0
        this.crud.form.customer = null
        this.crud.form.instances_server_id = 0
        this.crud.form.instances_server = null
        this.crud.form.instances_server_backups_id = 0
        this.crud.form.instances_server_backups = null
        this.crud.form.services_customers_details_id = 0
        this.crud.form.services_customers_details = null
        this.crud.form.categories_id = 0
        this.crud.form.categories = null
        this.crud.form.observations = ''
        this.crud.form.status_system = false
        this.crud.form.status_site = false
        this.crud.form.autocontrol = false
        this.crud.form.control_backups = false
        this.crud.form.observations_backups = ''
        this.crud.form.own = true
        this.crud.form.type = 'zyncro'

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Sistema"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.customer_id = item.customer.id
        this.crud.form.customer = item.customer
        this.crud.form.instances_server_id = item.instances_server_id
        this.crud.form.instances_server = item.server
        this.crud.form.instances_server_backups_id = item.instances_server_backups_id
        this.crud.form.instances_server_backups = item.server_backups
        this.crud.form.services_customers_details_id = item.services_customers_details_id
        this.crud.form.services_customers_details = item.services_customers_details
        this.crud.form.categories_id = item.categories_id
        this.crud.form.categories = item.categorie
        this.crud.form.observations = item.observations
        this.crud.form.autocontrol = item.autocontrol
        this.crud.form.control_backups = item.control_backups
        this.crud.form.observations_backups = item.observations_backups
        this.crud.form.own = true
        this.crud.form.type = 'zyncro'

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Sistema"
        this.modal.form.active = true
      },
      remove(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name        

        this.confirmDialog('¿Desea borrar el sistema (' + this.crud.form.id + ') - '+ this.crud.form.name + '?', {
          title: 'Borrar Sistema',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarSistema(this.crud.form.id);

            result.then(() => {
              this.modal.form.active = false        
              loader.hide()
              this.filter()
              this.$awn.success("Sistema eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      save() {
        let loader = this.$loading.show();
        var result = null

        if (this.crud.form.id) {
          result = serviceAPI.editarSistema(this.crud.form);
        } else {
          result = serviceAPI.agregarSistema(this.crud.form);
        }

        result.then(() => {
          this.modal.form.active = false
          loader.hide()
          this.filter()
          this.$awn.success("Cambios guardado con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      }, 

      // render
      showConfigurateBackupsActive() {
        var status = true

        if(!this.selectedSystem.backups) {
          status = false
        } else {
          if(JSON.parse(this.selectedSystem.backups).status=='Inactivo') {
            status = false
          }
        }
        
        return status
      },                 
      showSiteWebActionsDropdown() {
        if(!this.selectedSystem || !this.selectedSystem.status_install) {
          return false
        }

        return this.selectedSystem.status_install_site || !this.selectedSystem.blocked
      },
      blockedSystem(item) {
        this.confirmDialog('¿Desea bloquear el sistema (' + item.name + ')?', {
          title: 'Bloquear sistema',
          icon: 'lock',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {        
            let loader = this.$loading.show();

            var result = serviceAPI.commandBlockedSistema({
              id: item.id,
              blocked: true,
            });
          
            result.then(() => {
              loader.hide()
              this.filter()
              this.$awn.success("Bloqueo con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })            
      },
      unlockedSystem(item) {
        this.confirmDialog('¿Desea desbloquear el sistema (' + item.name + ')?', {
          title: 'Desbloquear sistema',
          icon: 'lock_open',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {        
            let loader = this.$loading.show();

            var result = serviceAPI.commandBlockedSistema({
              id: item.id,
              blocked: false,
            });
          
            result.then(() => {
              loader.hide()
              this.filter()
              this.$awn.success("Desbloqueo con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })            
      },

      // SELECT
      loadCustomers(object) {        
        if(object){
          this.crud.form.customer = object
          this.crud.form.customer_id = object.id                 
        } else {
          this.crud.form.customer = null
          this.crud.form.customer_id = 0
        }
      },
      loadInstancesServer(object) {
        if(object){
          this.crud.form.instances_server = object
          this.crud.form.instances_server_id = object.id                 
        } else {
          this.crud.form.instances_server = null
          this.crud.form.instances_server_id = 0
        }        
      },
      loadInstancesServerBackups(object) {
        if(object){
          this.crud.form.instances_server_backups = object
          this.crud.form.instances_server_backups_id = object.id                 
        } else {
          this.crud.form.instances_server_backups = null
          this.crud.form.instances_server_backups_id = 0
        }        
      },  
      loadServicesCustomersDetails(object) {
        if(object){
          this.crud.form.services_customers_details = object
          this.crud.form.services_customers_details_id = object.id                 
        } else {
          this.crud.form.services_customers_details = null
          this.crud.form.services_customers_details_id = 0
        }  
      },

      // fields server table 
      showFieldSystem(item, type) {
        var data = null
        var obj = null
       
        if(type == 'backups') {          
          obj = JSON.parse(item.backups)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        if(type == 'audit') {          
          obj = JSON.parse(item.audit)

          if(obj && obj.status) {
            data = obj.status
          }
        }

        if(type == 'access') {          
          obj = JSON.parse(item.access)

          if(obj && obj.status) {
            data = obj.status
          }
        }        

        return data
      },

      // filter select
      filterLoadSelects() {
        this.filterLoadCustomers();
        this.filterLoadServers();
        this.filterLoadCategories();
      },
      filterLoadCustomers(){
        var result = serviceAPI.obtenerClientes()        
        result.then((response) => {
          var data = response.data    

          let arrActive = []
          let arrInactive = []          
          data.forEach(element => {            
            if(element.active)
              arrActive.push({ code: element.id, label: element.name })            
            else 
              arrInactive.push({ code: element.id, label: '[INACTIVO] - ' + element.name })                                    
          });      
          this.arr.select.customers = arrActive.concat(arrInactive);           
        })   
      },
      filterLoadServers() {
        var result = serviceAPI.obtenerServidor()
        result.then((response) => {
          var data = response.data

          data.forEach(element => {
            if(element.type=='fullcontrol') {
              this.arr.select.instances_servers.push({code: element.id, label: element.name})
            }
          });
        })
      },
      filterLoadCategories() {
        var result = serviceAPI.obtenerCategoriesByType(this.filterType)
        result.then((response) => {
          var data = response.data
          data.forEach(element => {
            this.arr.select.categories.push({code: element.id, label: element.name})
          });
        })
      },

      // select categories
      loadInstancesCategories(object) {
        if(object){
          this.crud.form.categories = object
          this.crud.form.categories_id = object.id
        } else {
          this.crud.form.categories = null
          this.crud.form.categories_id = 0
        }
      },

      // action
      actionSystem(item) {
        this.selectedServer = item.server
        this.selectedSystem = item

        this.SSHConsole = ''

        this.modal.command._id = Math.random().toString(36).slice(2, 8)
        this.modal.command.title = "Comandos Sistema: [" + item.name + "]"
        this.modal.command.subtitle = "Terminal"

        this.modal.command.active = true
      },
      closeActionSystem() {
        this.modal.command.active=false
        this.filter()
      },
      copySSHConsole() {
        var consoleElement = this.$refs.sshConsole
        var output = consoleElement ? (consoleElement.innerText || consoleElement.textContent || '') : ''
        if (!output) {
          this.$awn.alert('No hay salida para copiar')
          return
        }

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(output)
            .then(() => this.$awn.success('Salida copiada al portapapeles'))
            .catch(() => this.copySSHConsoleFallback(output))
          return
        }

        this.copySSHConsoleFallback(output)
      },
      copySSHConsoleFallback(output) {

        var textarea = document.createElement('textarea')
        textarea.value = output
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        textarea.setSelectionRange(0, textarea.value.length)

        var copied = document.execCommand('copy')
        textarea.remove()
        copied ? this.$awn.success('Salida copiada al portapapeles') : this.$awn.alert('No se pudo copiar la salida')
      },
      refreshSystem() {                
        var result = serviceAPI.mostrarSistema(this.selectedSystem.id);
        
        result.then((response) => {
          var data = response.data

          this.selectedSystem = data

          this.$awn.success("Sistema refrescado");
        })        
      },
 
      getActionSystem(reference, titulo) {
        // titulo de referencia
        this.modal.command.subtitle = titulo

        // si apunta un dominio para el sistema, lo maneja otro evento
        if(reference == 'system-domain') {
          this.domainSystem(reference)
          return false
        }

        // si disable el sistema, lo maneja otro evento
        if(reference == 'system-disable') {
          this.disableSystem(reference)
          return false
        }

        // si enable el sistema, lo maneja otro evento
        if(reference == 'system-enable') {
          this.enableSystem(reference)
          return false
        }

        // si instala el sistema, lo maneja otro evento
        if(reference == 'system-install') {
          this.installSystem(reference)
          return false
        }

        // si actualiza el sistema, lo maneja otro evento
        if(reference == 'system-update') {
          this.updateSystem(reference)
          return false
        }

        // si desinstala el sistema, lo maneja otro evento
        if(reference == 'system-uninstall') {
          this.uninstallSystem(reference)
          return false
        }

        // si apunta un dominio para el sitio web, lo maneja otro evento
        if(reference == 'system-web-domain') {
          this.domainSite(reference)
          return false
        }

        // si disable el sitio web, lo maneja otro evento
        if(reference == 'system-web-disable') {
          this.disableWebSystem(reference)
          return false
        }

        // si enable el sitio web, lo maneja otro evento
        if(reference == 'system-web-enable') {
          this.enableWebSystem(reference)
          return false
        }

        // si instala el sitio web, lo maneja otro evento
        if(reference == 'system-web-install') {
          this.installWebSystem(reference)
          return false
        }

        // si actualiza el sitio web, lo maneja otro evento
        if(reference == 'system-web-update') {
          this.updateWebSystem(reference, false)
          return false
        }

        // si desinstala el sitio web, lo maneja otro evento
        if(reference == 'system-web-uninstall') {
          this.uninstallWebSystem(reference)
          return false
        }

        // si instala un producto, lo maneja otro evento
        if(reference.includes('system-install-products')) {
          this.installProductsSystem(reference, titulo)
          return false          
        }

        // si configura el backups, lo maneja otro evento
        if(reference == 'system-backups-configurate') {
          this.backupsConfigurateSystem(reference)
          return false
        }

        // si borro el backups, lo maneja otro evento
        if(reference == 'system-backups-delete') {
          this.backupsDeleteSystem(reference)
          return false
        }        

        // si borro el log del sistema, lo maneja otro evento
        if(reference == 'system-clean-last-log-error') {
          this.cleanLogErrorSystem(reference)
          return false
        }        

        // si borro el log del sitio web, lo maneja otro evento
        if(reference == 'system-web-clean-log-error') {
          this.cleanLogErrorSite(reference)
          return false
        }        

        // comandos
        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 

        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        }

        this.SSHLoader = true
        var result = serviceAPI.commandActionSistema({
          id: this.selectedServer.id, 
          command: reference,
          reference: this.selectedSystem.name,
          domain: this.selectedServer.subdomain,
          system_id: this.selectedSystem.id,
        });

        result.then((response) => {                    
          var data = response.data

          this.SSHConsole = data
          this.filter()
          this.SSHLoader = false
        })
        .catch(error => {          
          this.SSHLoader = false
          this.$awn.alert(Error.showError(error));
        })
      },
      disableSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea deshabilitar el sistema (' + item.name + ')?', {
          title: 'Deshabilitar sistema',
          icon: 'pause_circle',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la deshabilitación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })    
      },
      enableSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea habilitar el sistema (' + item.name + ')?', {
          title: 'Habilitar sistema',
          icon: 'play_circle',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la habilitación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })  
      },
      installSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea instalar el sistema (' + item.name + ')?', {
          title: 'Instalar Sistema',
          icon: 'download',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {   
              this.SSHConsole = 'Problemas en la instalación'           
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },   
      updateSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }

        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }

        var msj = 'Desea actualizar la versión del sistema'
        var title = 'Actualizar sistema'

        this.confirmDialog('¿' + msj + ' (' + item.name + ')?', {
          title: title,
          icon: 'upgrade',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {   
              this.SSHConsole = 'Problemas en la actualización'           
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },          
      uninstallSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea desinstalar el sistema (' + item.name + ')?', {
          title: 'Desinstalar sistema',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la desinstalación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })       
      },    
      disableWebSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 

        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea deshabilitar el sitio web (' + item.name + ')?', {
          title: 'Deshabilitar sitio web',
          icon: 'pause_circle',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la deshabilitación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })    
      },
      enableWebSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }    

        this.confirmDialog('¿Desea habilitar el sitio web (' + item.name + ')?', {
          title: 'Habilitar sitio web',
          icon: 'play_circle',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la habilitación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })  
      },          
      installWebSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea instalar el sitio web para el sistema (' + item.name + ')?', {
          title: 'Instalar sitio web',
          icon: 'download',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {   
              this.SSHConsole = 'Problemas en la instalación'           
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },   
      updateWebSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea actualizar la versión del sitio web para el sistema (' + item.name + ')?', {
          title: 'Actualizar versión sitio web',
          icon: 'upgrade',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {   
              this.SSHConsole = 'Problemas en la actualización'           
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },          
      uninstallWebSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea desinstalar el sitio web para el sistema (' + item.name + ')?', {
          title: 'Desinstalar sitio web',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en la desinstalación'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })       
      },        
      installProductsSystem(reference, product) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }                  
                
        this.confirmDialog('¿Desea instalar el producto ['+ product +'] en el sistema (' + item.name + ')?', {
          title: 'Instalar Producto',
          icon: 'inventory_2',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {   
              this.SSHConsole = 'Problemas en la instalación'           
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })          
      },

      backupsConfigurateSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 

        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }            
                
        this.confirmDialog('¿Desea configurar el backups para el sistema (' + item.name + ')?', {
          title: 'Configurar Backups',
          icon: 'backup',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en configurar el backups'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        }) 
      },
      backupsDeleteSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }

        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }    
                
        this.confirmDialog('¿Desea quitar el backups para el sistema (' + item.name + ')?', {
          title: 'Quitar Backups',
          icon: 'backup',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en quitar el backups'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })  
      },

      cleanLogErrorSystem(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }        
                
        this.confirmDialog('¿Desea limpiar el log de errores para el sistema (' + item.name + ')?', {
          title: 'Limpiar Log de Errores',
          icon: 'cleaning_services',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en limpiar el log de errores'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })  
      },
      cleanLogErrorSite(reference) {
        var item = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        } 
        
        if(!this.selectedSystem) {
          this.$awn.alert("No se encontró el sistema");
          return false
        } else {
          item = this.selectedSystem
        }        
                
        this.confirmDialog('¿Desea limpiar el log de errores para el sitio (' + item.name + ')?', {
          title: 'Limpiar Log de Errores',
          icon: 'cleaning_services',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.SSHLoader = true
            var result = serviceAPI.commandActionSistema({
              id: this.selectedServer.id, 
              command: reference,
              reference: this.selectedSystem.name,
              domain: this.selectedServer.subdomain,
              system_id: this.selectedSystem.id,
            });

            result.then((response) => {   
              var data = response.data

              this.SSHConsole = data
              this.filter()
              this.SSHLoader = false
            })
            .catch(error => {              
              this.SSHConsole = 'Problemas en limpiar el log de errores'
              this.SSHLoader = false
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })          
      },

      domainSystem() {
        this.crud.formSystem.id = this.selectedSystem.id
        this.crud.formSystem.domain = this.selectedSystem.domain

        this.modal.formSystem.title = "Apuntar Dominio a Sistema: [" + this.selectedSystem.name + "]"        
        this.modal.formSystem.active = true
      },
      domainSite() {
        this.crud.formSite.id = this.selectedSystem.id
        this.crud.formSite.domain = this.selectedSystem.domain_site

        this.modal.formSite.title = "Apuntar Dominio a Sitio Web: [" + this.selectedSystem.name + "]"        
        this.modal.formSite.active = true        
      },      
      saveDomain(type) {
        this.modal.formSystem.active = false
        this.modal.formSite.active = false

        // comandos
        var reference = ''
        var newDomain = null

        if(!this.selectedServer) {
          this.$awn.alert("No se encontró el servidor");
          return false
        }

        if(type=='system') {
          reference = 'system-domain'
          newDomain = this.crud.formSystem
        } 
        if(type=='site') {
          reference = 'system-web-domain'
          newDomain = this.crud.formSite
        }         

        this.SSHLoader = true
        var result = serviceAPI.commandActionSistema({
          id: this.selectedServer.id, 
          command: reference,
          reference: this.selectedSystem.name,
          domain: this.selectedServer.subdomain,
          system_id: this.selectedSystem.id,
          newDomain: newDomain,
        });

        result.then((response) => {                    
          var data = response.data

          this.SSHConsole = data
          this.filter()
          this.SSHLoader = false          
        })
        .catch(error => {          
          this.SSHLoader = false
          this.$awn.alert(Error.showError(error));
        })
      },   

      // FILTROS SAVE
      saveFilterStorage() {
        Storage.setValue('filter_view_instances_system', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_view_instances_system')) {
          this.filters = JSON.parse(Storage.getValue('filter_view_instances_system'))
        }
        if(!this.filters.health) {
          this.filters.health = { code: 'all', label: 'Todos' }
        }
      },       
    }    
  }
</script>
<style>
  .ssh-console-system {
    background-color: black;
    background-image: radial-gradient(
      rgba(0, 150, 0, 0.75), black 120%
    );
    height: 175px;
    color: white;
    font: 13px Inconsolata, monospace;    
    text-shadow: 0 0 5px #C8C8C8;
    padding: 10px;
    white-space: pre;
    overflow: auto;
  }
  .ssh-console-system::after {
    content: "";
    /*position: absolute;*/
    top: 0;
    left: 0;
    width: 100vw;
    height: 175px;
    background: repeating-linear-gradient(
      0deg,
      rgba(black, 0.15),
      rgba(black, 0.15) 1px,
      transparent 1px,
      transparent 2px
    );
    padding: 10px;
  }  
  .ssh-console-system::selection {
    background: #0080FF;
    text-shadow: none;
  }  
  .ssh-console-footer {
    z-index: 9999;
  }
  .app-command-terminal-column { display: flex; }
  .app-command-terminal { display: flex; flex: 1; flex-direction: column; min-width: 0; }
  .app-command-terminal__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 36px; margin-bottom: 8px; }
  .app-command-terminal__header h4 { margin: 0; color: var(--app-color-text); font-size: 20px; font-weight: 600; line-height: 1.2; }
  .app-command-terminal__actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; min-height: 28px; }
  .app-command-terminal__process { display: inline-flex; align-items: center; gap: 4px; color: var(--app-color-primary); font-size: 12px; font-weight: 600; }
  .btn.app-command-terminal__copy { display: inline-flex; align-items: center; height: 28px; padding: 0 8px; border-radius: 4px; font-size: 12px; }
  .btn.app-command-terminal__refresh { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 28px; padding: 0; border-radius: 4px; }
  .app-command-terminal__loader { display: flex; flex: 1; align-items: center; justify-content: center; min-height: 175px; background: #071009; }
  .ssh-console-system { flex: 0 0 420px; box-sizing: border-box; height: 420px !important; max-height: calc(100vh - 220px); padding: 10px; overflow-x: auto; overflow-y: auto !important; border-radius: 0; background-color: #071009; background-image: radial-gradient(ellipse at center, #173b1a 0%, #0b1d0e 52%, #071009 100%); color: #fff; font: 13px Inconsolata, monospace; line-height: 1.45; overflow-wrap: anywhere; text-shadow: none; }
  .ssh-console-system::after { display: none; }
  @media (max-width: 767.98px) { .app-command-terminal__header { align-items: flex-start; }.app-command-terminal__header h4 { font-size: 18px; }.ssh-console-system { flex-basis: 320px; height: 320px !important; max-height: calc(100vh - 190px); } }
  .instance-system-divide {
    margin-top: 1px;
    margin-bottom: 10px;    
  }  
  .instances-system-command-panel-column { display: flex; }
  .instances-system-command-panel {
    width: 100%;
    max-height: 450px;
    padding: 10px;
    overflow-y: auto;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }
  .instances-system-command-panel h6 {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 2px 4px 9px;
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  .instances-system-command-group { border-top: 1px solid var(--app-color-border); }
  .instances-system-command-group:last-child { border-bottom: 1px solid var(--app-color-border); }
  .btn.instances-system-command-group__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 36px;
    padding: 7px 4px;
    color: var(--app-color-text);
    font-size: 13px;
    font-weight: 700;
    text-align: left;
  }
  .instances-system-command-group__toggle > span,
  .btn.instances-system-command-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .instances-system-command-group__toggle:hover,
  .instances-system-command-group__toggle:focus {
    color: var(--app-color-primary);
    text-decoration: none;
  }
  .instances-system-command-group__actions { display: grid; gap: 2px; padding: 0 0 8px; }
  .instances-system-command-subsection {
    display: block;
    margin: 8px 0 3px;
    padding: 5px 7px;
    border-top: 1px solid var(--app-color-border);
    border-left: 3px solid var(--app-color-primary);
    background: var(--app-color-surface-muted);
    color: var(--app-color-text-secondary);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  .instances-system-command-subsection:first-child { margin-top: 1px; }
  .btn.instances-system-command-action {
    min-height: 31px;
    padding: 5px 7px;
    color: var(--app-color-text-secondary);
    font-size: 12px;
    text-align: left;
  }
  .btn.instances-system-command-action:hover,
  .btn.instances-system-command-action:focus {
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    text-decoration: none;
  }
  .btn.instances-system-command-action--danger:hover,
  .btn.instances-system-command-action--danger:focus {
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger-hover);
  }
  .instances-system-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 240px;
    color: var(--app-color-text-secondary);
    font-size: 13px;
  }
  .instances-system-groups {
    display: grid;
    gap: 16px;
  }
  .instances-system-group-card {
    overflow: hidden;
    border-color: var(--app-color-border);
    border-radius: 10px;
    box-shadow: var(--app-shadow-sm);
  }
  .instances-system-group-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: 0;
    border-bottom: 1px solid var(--app-color-border);
    background: var(--app-color-surface-subtle);
    color: inherit;
    text-align: left;
  }
  .instances-system-group-card__header:hover { background: var(--app-color-primary-soft); }
  .instances-system-group-card__header--static { cursor: default; }
  .instances-system-group-card__header--static:hover { background: var(--app-color-surface-subtle); }
  .instances-system-group-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  .instances-system-group-card__header h3,
  .instances-system-card__title h4 {
    margin: 0;
    color: var(--app-color-text);
  }
  .instances-system-group-card__header h3 { font-size: 14px; line-height: 1.15; }
  .instances-system-group-card__summary > span {
    color: var(--app-color-text-secondary);
    font-size: 11px;
  }
  .instances-system-group-card__health {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
    margin-left: auto;
  }
  .instances-system-group-card__health-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 7px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    white-space: nowrap;
  }
  .instances-system-group-card__health-item .material-symbols-outlined { font-size: 15px; }
  .instances-system-group-card__health-item--success {
    border-color: var(--app-color-success-border);
    background: var(--app-color-success-soft);
    color: var(--app-color-success-hover);
  }
  .instances-system-group-card__health-item--warning {
    border-color: var(--app-color-warning-border);
    background: var(--app-color-warning-soft);
    color: var(--app-color-warning-hover);
  }
  .instances-system-group-card__health-item--danger {
    border-color: var(--app-color-danger-border);
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger-hover);
  }
  .instances-system-group-card__health-item--info {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  .instances-system-group-card__toggle {
    margin-left: auto;
    color: var(--app-color-text-secondary);
  }
  .instances-system-group-card__body {
    padding: 10px;
    background: var(--app-color-canvas);
  }
  .instances-system-group-card__cards { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:10px; }
  .instances-system-view-switcher { display:inline-flex; align-items:center; gap:1px; margin-right:7px; padding-right:7px; border-right:1px solid var(--app-color-border); }
  .instances-system-view-switcher .btn.list-view-icon-btn.list-view-icon-btn--active { background:var(--app-color-primary-soft); color:var(--app-color-primary); }
  .instances-system-tree-view { display:grid; gap:2px; }
  .instances-system-tree-view__system { display:grid; grid-template-columns:32px minmax(190px, .8fr) minmax(0, 2.2fr); align-items:center; min-height:46px; border-bottom:1px solid var(--app-color-border); background:var(--app-color-surface); }
  .instances-system-tree-view__system:last-child { border-bottom:0; }
  .instances-system-tree-view__branch { display:inline-flex; align-self:stretch; align-items:center; justify-content:center; color:var(--app-color-primary); }
  .instances-system-tree-view__header { display:flex; align-items:center; justify-content:space-between; gap:8px; min-width:0; padding:7px 8px 7px 0; }
  .instances-system-tree-view__details { display:flex; flex-wrap:wrap; align-items:center; gap:12px; padding:7px 12px; }
  .instances-system-tree-view__item { display:inline-flex; align-items:center; gap:4px; cursor:pointer; font-size:12px; font-weight:600; }
  .instances-system-tree-view__item .material-symbols-outlined { font-size:17px; }.instances-system-tree-view__item b { color:var(--app-color-text-secondary); font-weight:600; }
  .instances-system-tree-view__item.text-danger { padding:3px 6px; border:1px solid var(--app-color-danger-border); border-radius:4px; background:var(--app-color-danger-soft); color:var(--app-color-danger-hover); }.instances-system-tree-view__item.text-danger b { color:inherit; }
  .instances-system-tree-view__link, .instances-system-tree-view__more { display:inline-flex; align-items:center; gap:3px; font-size:11px; white-space:nowrap; }.instances-system-tree-view__more { cursor:pointer; color:var(--app-color-primary); }.instances-system-tree-view__more .material-symbols-outlined { font-size:18px; }
  .instances-system-card {
    padding: 12px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface);
    box-shadow: var(--app-shadow-sm);
  }
  .instances-system-card--blocked {
    border-color: var(--app-color-border);
    background: var(--app-color-surface);
  }
  .instances-system-card--unblocked {
    border-color: var(--app-color-danger-border);
    background: var(--app-color-danger-soft);
  }
  .instances-system-card__heading {
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .instances-system-card__title,
  .instances-system-card__meta,
  .instances-system-card__links,
  .instances-system-card__alert {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .instances-system-card__title h4 { font-size: 17px; }
  .instances-system-card__id {
    color: var(--app-color-text-secondary);
    font-size: 12px;
    font-weight: 600;
  }
  .instances-system-card__meta {
    flex-wrap: wrap;
    margin-top: 6px;
    color: var(--app-color-text-secondary);
    font-size: 12px;
  }
  .instances-system-card__infrastructure {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }
  .instances-system-card__customer,
  .instances-system-card__server {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  .instances-system-card__server {
    padding: 3px 6px;
    border: 1px solid var(--app-color-border);
    border-radius: 999px;
    background: var(--app-color-surface);
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 600;
  }
  .instances-system-card__service {
    max-width: 110px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .instances-system-card__proxy {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
  }
  .instances-system-card__tooltip-icon,
  .instances-system-status,
  .instances-system-check {
    cursor: pointer;
  }
  .instances-system-card__alerts {
    display: grid;
    gap: 8px;
    margin-top: 12px;
  }
  .instances-system-card__alert {
    align-items: flex-start;
    padding: 8px 10px;
    border: 1px solid var(--app-color-border);
    border-left: 4px solid var(--app-color-primary);
    border-radius: 4px;
  }
  .instances-system-card__alert--backup { border-left-color: var(--app-color-warning); }
  .instances-system-card__alert div { display: grid; gap: 2px; min-width: 0; }
  .instances-system-card__alert b { font-size: 12px; }
  .instances-system-card__alert span { color: var(--app-color-text-secondary); font-size: 12px; line-height: 1.35; }
  .instances-system-card__links {
    flex-wrap: wrap;
    margin-top: 12px;
    font-size: 12px;
  }
  .instances-system-card__links a { display: inline-flex; align-items: center; gap: 3px; }
  .instances-system-card__metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--app-color-border);
  }
  .instances-system-card__section {
    display: grid;
    align-content: start;
    justify-items: center;
    gap: 7px;
    min-width: 0;
  }
  .instances-system-card__section--controls {
    grid-column: 1 / -1;
  }
  .instances-system-card__section-label {
    color: var(--app-color-text-secondary);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .03em;
    text-transform: uppercase;
  }
  .instances-system-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    font-size: 11px;
  }
  .instances-system-card__meta .material-symbols-outlined,
  .instances-system-card__infrastructure .material-symbols-outlined,
  .instances-system-card__links .material-symbols-outlined {
    font-size: 18px;
  }
  .instances-system-checks {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 7px;
  }
  .instances-system-check {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 52px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.1;
    text-align: center;
  }
  .instances-system-check small { color: var(--app-color-text-secondary); font-size: 10px; font-weight: 400; }
  .app-modal-domain-guide {
    height: 100%;
    padding: 16px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text-secondary);
    font-size: 13px;
    line-height: 1.55;
  }
  .app-modal-domain-guide__title {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    color: var(--app-color-text);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .03em;
    text-transform: uppercase;
  }
  .app-modal-domain-guide p { margin-bottom: 10px; }
  .app-modal-domain-guide li + li { margin-top: 4px; }
  @media (max-width: 767.98px) {
    .instances-system-group-card__header { align-items: flex-start; flex-wrap: wrap; }
    .instances-system-group-card__health {
      order: 3;
      flex-basis: calc(100% - 34px);
      justify-content: flex-start;
      margin-left: 34px;
    }
    .instances-system-group-card__toggle { margin-left: auto; }
    .instances-system-group-card__body { padding: 8px; }
    .instances-system-group-card__cards { grid-template-columns: 1fr; }
    .instances-system-tree-view__system { grid-template-columns:27px minmax(0, 1fr); }.instances-system-tree-view__details { grid-column:2; padding:0 8px 9px 0; gap:8px; }
    .instances-system-card { padding: 13px; }
    .instances-system-card__metrics { grid-template-columns: 1fr; gap: 14px; }
    .instances-system-card__section { justify-items: start; }
    .instances-system-checks { justify-content: flex-start; }
  }
  #instances_system_table .table-dark {
    background-color: #676363 !important;
  }
  #instances_system_table .table-dark, .table-dark > th, .table-dark > td  {
      background-color: #676363 !important;
  }    

  .intances-audit-pre-json {
    overflow: auto;    
    height: 300px;
    width: 100%;
    min-width: unset;
  }
  .json-tree-value {
      overflow: unset;
      text-overflow: unset;
      white-space: nowrap;
  }  
  .json-tree-collapsed {
      overflow: unset;
      text-overflow: unset;
      white-space: nowrap;    
  }  
</style>
<style scoped>
  .b-avatar {
    width: 25px;
    height: 25px;    
  }  
</style>
